import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth"
import { db } from "../services/firebase"
import { useUser } from "../contexts/UserContext"
import { collection, query, where, onSnapshot, deleteDoc, doc } from "firebase/firestore"
import NoPermissionOverlay from "./NoPermissionOverlay"
import ProfileSidebar from "../components/ProfileSideBar"
import EmailTemplateModal from "./EmailTemplateModal"
import SupportEmailModal from "./SupportEmailModal"
import teamsIcon  from "../assets/icons/teams.png"
import meetIcon   from "../assets/icons/meet.png"
import falconIcon from "../assets/icons/falcon.png"
import { Mail, Trash2, Plug, Eye, EyeOff, Plus, Loader2 } from "lucide-react"

const FF = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const T = {
  border:      "0.5px solid #e5e7eb",
  borderLight: "0.5px solid #f0f0ef",
  radius:      6,
  bg:          "#fff",
  text:        "#111827",
  textMuted:   "#6b7280",
  textSub:     "#9ca3af",
}

// ── Primitives ────────────────────────────────────────────────────────────────

const Btn = ({ onClick, children, variant = "ghost", disabled, type }) => {
  const s = {
    primary: { bg: "#111827", color: "#fff",    border: "none"                     },
    ghost:   { bg: "transparent", color: "#374151", border: T.border              },
    danger:  { bg: "#fef2f2",  color: "#dc2626", border: "0.5px solid #fecaca"   },
  }[variant]
  return (
    <button
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        height: 32, padding: "0 14px",
        borderRadius: T.radius, border: s.border,
        background: s.bg, color: s.color,
        fontSize: 13, fontWeight: 600, fontFamily: FF,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap", flexShrink: 0, outline: "none",
      }}
    >
      {children}
    </button>
  )
}

const Toggle = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    style={{
      position: "relative", width: 40, height: 22,
      borderRadius: 11, border: "none",
      background: checked ? "#111827" : "#e5e7eb",
      cursor: "pointer", outline: "none", flexShrink: 0,
      transition: "background 0.18s",
    }}
  >
    <span style={{
      position: "absolute", top: 3,
      left: checked ? 19 : 3,
      width: 16, height: 16, borderRadius: "50%",
      background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      transition: "left 0.18s",
    }} />
  </button>
)

const Section = ({ title, right, children, style }) => (
  <div style={{ marginBottom: 36, ...style }}>
    {title && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 10, borderBottom: T.border, marginBottom: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {title}
        </p>
        {right}
      </div>
    )}
    {children}
  </div>
)

const Row = ({ label, sub, right, last = false }) => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "13px 0",
    borderBottom: last ? "none" : T.borderLight,
  }}>
    <div style={{ minWidth: 0, flex: 1, marginRight: 16 }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: T.text, fontFamily: FF }}>{label}</p>
      {sub && <p style={{ margin: "2px 0 0", fontSize: 12, color: T.textMuted, fontFamily: FF }}>{sub}</p>}
    </div>
    {right && <div style={{ flexShrink: 0 }}>{right}</div>}
  </div>
)

const FieldInput = ({ label, type = "text", value, onChange, placeholder, adornment }) => (
  <div>
    {label && <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 500, color: T.text, fontFamily: FF }}>{label}</p>}
    <div style={{ position: "relative" }}>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", height: 38, padding: adornment ? "0 38px 0 12px" : "0 12px",
          border: T.border, borderRadius: T.radius,
          fontSize: 14, color: T.text, fontFamily: FF,
          background: T.bg, outline: "none",
          transition: "border-color 0.12s", boxSizing: "border-box",
        }}
        onFocus={(e) => { e.target.style.borderColor = "#9ca3af" }}
        onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb" }}
      />
      {adornment && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
          {adornment}
        </div>
      )}
    </div>
  </div>
)

const Alert = ({ type = "info", children }) => {
  const c = {
    info:    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
    error:   { bg: "#fef2f2", border: "#fecaca", text: "#dc2626" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
    warn:    { bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
  }[type]
  return (
    <div style={{
      padding: "10px 14px", borderRadius: T.radius,
      background: c.bg, border: `0.5px solid ${c.border}`,
      fontSize: 13, fontWeight: 500, color: c.text, fontFamily: FF, lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

const ConnectedBadge = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 11, fontWeight: 600, color: "#065f46",
    background: "#ecfdf5", border: "0.5px solid #a7f3d0",
    borderRadius: 4, padding: "2px 8px", fontFamily: FF,
  }}>
    <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981" }} />
    Connected
  </span>
)

const MeetLogo = ({ size = 22 }) => (
  <img src={meetIcon} alt="Google Meet" style={{ width: size, height: size, objectFit: "contain" }} />
)

const IntegRow = ({ logo, name, sub, isConnected, onConnect, onDisconnect, badge, connectLabel = "Connect", last = false }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 14,
    padding: "13px 0", borderBottom: last ? "none" : T.borderLight,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 8, border: T.border,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      {logo}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: FF }}>{name}</span>
        {isConnected && <ConnectedBadge />}
        {badge && (
          <span style={{ fontSize: 11, fontWeight: 500, color: T.textMuted, background: "#f5f5f4", border: T.border, borderRadius: 4, padding: "1px 7px", fontFamily: FF }}>
            {badge}
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: T.textMuted, fontFamily: FF, lineHeight: 1.4 }}>{sub}</p>
    </div>
    {isConnected && onDisconnect
      ? <Btn onClick={onDisconnect} variant="danger">Disconnect</Btn>
      : !isConnected && onConnect
        ? <Btn onClick={onConnect} variant="ghost"><Plug size={12} /> {connectLabel}</Btn>
        : null}
  </div>
)

// ── Content ───────────────────────────────────────────────────────────────────

const SecurityContent = () => {
  const [current, setCurrent]         = useState("")
  const [next, setNext]               = useState("")
  const [confirm, setConfirm]         = useState("")
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext]       = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [status, setStatus]           = useState(null)
  const [message, setMessage]         = useState("")
  const [loading, setLoading]         = useState(false)

  const handleSubmit = async () => {
    setStatus(null)
    if (!current || !next || !confirm) { setStatus("error"); setMessage("All fields are required."); return }
    if (next.length < 8)               { setStatus("error"); setMessage("Password must be at least 8 characters."); return }
    if (next !== confirm)              { setStatus("error"); setMessage("Passwords do not match."); return }
    setLoading(true)
    try {
      const auth = getAuth()
      const user = auth.currentUser
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current))
      await updatePassword(user, next)
      setStatus("success"); setMessage("Password updated.")
      setCurrent(""); setNext(""); setConfirm("")
    } catch (err) {
      const msgs = {
        "auth/wrong-password":    "Current password is incorrect.",
        "auth/too-many-requests": "Too many attempts. Try again later.",
        "auth/weak-password":     "Password must be at least 6 characters.",
      }
      setStatus("error"); setMessage(msgs[err.code] || "Failed to update password.")
    } finally { setLoading(false) }
  }

  const EyeBtn = ({ show, toggle }) => (
    <button type="button" onClick={toggle} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 0, display: "flex" }}>
      {show ? <EyeOff size={14} /> : <Eye size={14} />}
    </button>
  )

  return (
    <div style={{ maxWidth: 440 }}>
      <Section title="Password">
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 16 }}>
          <FieldInput label="Current password" type={showCurrent ? "text" : "password"} value={current} onChange={setCurrent} placeholder="••••••••" adornment={<EyeBtn show={showCurrent} toggle={() => setShowCurrent(v => !v)} />} />
          <FieldInput label="New password"     type={showNext    ? "text" : "password"} value={next}    onChange={setNext}    placeholder="••••••••" adornment={<EyeBtn show={showNext}    toggle={() => setShowNext(v => !v)} />} />
          <FieldInput label="Confirm password" type={showConfirm ? "text" : "password"} value={confirm} onChange={setConfirm} placeholder="••••••••" adornment={<EyeBtn show={showConfirm} toggle={() => setShowConfirm(v => !v)} />} />
          {status && <Alert type={status === "success" ? "success" : "error"}>{message}</Alert>}
          <div>
            <Btn onClick={handleSubmit} variant="primary" disabled={loading}>
              {loading ? <><Loader2 size={12} style={{ animation: "spin 0.8s linear infinite" }} /> Updating…</> : "Update password"}
            </Btn>
          </div>
        </div>
      </Section>

      <Section title="Sessions">
        <Row label="Two-factor authentication" sub="Add an extra layer of security to your account" right={<Btn variant="ghost">Enable</Btn>} />
        <Row label="Active sessions" sub="View and manage all active login sessions" right={<Btn variant="ghost">View</Btn>} last />
      </Section>
    </div>
  )
}

const NotificationsContent = ({ notifications, onChange }) => {
  const rows = [
    { key: "communicationEmails", label: "Communication emails",   sub: "Messages, contracts, and documents"    },
    { key: "announcements",       label: "Announcements",          sub: "Product updates and new features"      },
    { key: "interviewReminders",  label: "Interview reminders",    sub: "Upcoming interview alerts"             },
    { key: "candidateUpdates",    label: "Candidate updates",      sub: "Status changes and assessment results" },
  ]
  return (
    <Section title="Email notifications">
      {rows.map((r, i) => (
        <Row
          key={r.key}
          label={r.label}
          sub={r.sub}
          right={<Toggle checked={notifications[r.key]} onChange={(v) => onChange(r.key, v)} />}
          last={i === rows.length - 1}
        />
      ))}
    </Section>
  )
}

const IntegrationsContent = () => {
  const { loading, organizationName, userData } = useUser()

  const googleLinked = !!(userData?.google_calendar?.connected)
  const msLinked     = !!(userData?.ms_calendar?.connected)
  const [supportOpen, setSupportOpen] = useState(false)

  const userId = getAuth().currentUser?.uid

  const open = (url) => window.open(url, "_blank", "width=600,height=700")

  const gUrl = (state) => {
    const id    = process.env.REACT_APP_GOOGLE_CLIENT_ID || "YOUR_GOOGLE_CLIENT_ID"
    const uri   = encodeURIComponent("https://api.resolut.co.in/api/auth/google/personal/callback")
    const scope = encodeURIComponent("https://www.googleapis.com/auth/calendar.events")
    return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${id}&redirect_uri=${uri}&response_type=code&scope=${scope}&state=${encodeURIComponent(JSON.stringify(state))}&access_type=offline&prompt=consent`
  }

  const googleConnect    = () => { if (!userId) return; open(gUrl({ type: "personal", userId })) }
  const googleDisconnect = async () => { const { updateDoc, deleteField } = await import("firebase/firestore"); await updateDoc(doc(db, "users", userId), { google_calendar: deleteField() }).catch(console.error) }
  const msConnect = () => {
    const id    = process.env.REACT_APP_MS_CLIENT_ID || "YOUR_CLIENT_ID"
    const uri   = encodeURIComponent("https://api.resolut.co.in/api/auth/microsoft/personal/callback")
    const scope = encodeURIComponent("Calendars.ReadWrite offline_access")
    const st    = encodeURIComponent(JSON.stringify({ type: "personal", userId }))
    open(`https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${id}&redirect_uri=${uri}&response_type=code&scope=${scope}&state=${st}`)
  }
  const msDisconnect = async () => { const { updateDoc, deleteField } = await import("firebase/firestore"); await updateDoc(doc(db, "users", userId), { ms_calendar: deleteField() }).catch(console.error) }

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
      {[80, 60, 80, 60].map((w, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f0f0ef", animation: "shimmer 1.4s ease-in-out infinite" }} />
          <div style={{ flex: 1 }}>
            <div style={{ width: `${w}%`, height: 13, borderRadius: 4, background: "#f0f0ef", marginBottom: 6, animation: "shimmer 1.4s ease-in-out infinite" }} />
            <div style={{ width: "50%", height: 11, borderRadius: 4, background: "#f5f5f4", animation: "shimmer 1.4s ease-in-out infinite" }} />
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div>
      <SupportEmailModal
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
        companyName={organizationName || ""}
        defaultSubject="Request to enable Falconhq"
      />

      {/* Recruiter section */}
      <Section title="Recruiter">
        <p style={{ margin: "10px 0 0", paddingBottom: 12, fontSize: 12, color: T.textMuted, fontFamily: FF, lineHeight: 1.6, borderBottom: T.border }}>
          Connect your calendar so interviews you schedule are automatically added as events — with the candidate and interviewer invited and a meeting link ready to go.
        </p>
        <IntegRow
          logo={<MeetLogo />}
          name="Google Meet"
          sub="Creates a Google Calendar event with a Meet link — candidate and interviewer are invited automatically"
          isConnected={googleLinked}
          onConnect={googleConnect}
          onDisconnect={googleDisconnect}
        />
        <IntegRow
          logo={<img src={teamsIcon} alt="Outlook" style={{ width: 22, height: 22, objectFit: "contain" }} />}
          name="Microsoft Outlook"
          sub="Creates an Outlook event with a Teams meeting link — candidate and interviewer are invited automatically"
          isConnected={msLinked}
          onConnect={msConnect}
          onDisconnect={msDisconnect}
        />
        <IntegRow
          logo={<img src={falconIcon} alt="Falconhq" style={{ width: 22, height: 22, objectFit: "contain" }} />}
          name="Falconhq"
          sub="Our built-in interview platform — interviews run directly on Resolut with AI assistance"
          isConnected={false}
          onConnect={() => setSupportOpen(true)}
          connectLabel="Request Access"
          last
        />
      </Section>

    </div>
  )
}

const EmailTemplatesContent = ({ userId }) => {
  const [templates, setTemplates]   = useState([])
  const [loading, setLoading]       = useState(true)
  const [modalOpen, setModalOpen]   = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const { organizationId, isInterviewer } = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (!organizationId) return
    const q = query(collection(db, "emailTemplates"), where("organizationId", "==", organizationId))
    const unsub = onSnapshot(q, (snap) => {
      setTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      setLoading(false)
    }, () => setLoading(false))
    return () => unsub()
  }, [organizationId])

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return
    setDeletingId(id)
    try { await deleteDoc(doc(db, "emailTemplates", id)) }
    catch (e) { console.error(e) }
    finally { setDeletingId(null) }
  }

  return (
    <>
      <EmailTemplateModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => {}} userId={userId} />
      <NoPermissionOverlay isOpen={isInterviewer} onClose={() => navigate("/interviews")} />

      <Section
        title={loading ? "Templates" : `Templates · ${templates.length}`}
        right={<Btn onClick={() => setModalOpen(true)} variant="primary"><Plus size={13} /> New template</Btn>}
      >
        {loading ? (
          <div style={{ padding: "24px 0", display: "flex", alignItems: "center", gap: 8 }}>
            <Loader2 size={14} color={T.textMuted} style={{ animation: "spin 0.8s linear infinite" }} />
            <span style={{ fontSize: 13, color: T.textMuted, fontFamily: FF }}>Loading…</span>
          </div>
        ) : templates.length === 0 ? (
          <div style={{ padding: "48px 0", textAlign: "center" }}>
            <Mail size={20} color="#d1d5db" style={{ margin: "0 auto 10px", display: "block" }} />
            <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 500, color: T.text, fontFamily: FF }}>No templates yet</p>
            <p style={{ margin: 0, fontSize: 12, color: T.textMuted, fontFamily: FF }}>Create your first email template to get started</p>
          </div>
        ) : (
          templates.map((t, i) => (
            <Row
              key={t.id}
              label={t.name}
              sub={t.subject}
              right={
                <Btn onClick={() => handleDelete(t.id)} variant="danger" disabled={deletingId === t.id}>
                  {deletingId === t.id ? "…" : <><Trash2 size={12} /> Delete</>}
                </Btn>
              }
              last={i === templates.length - 1}
            />
          ))
        )}
      </Section>
    </>
  )
}

const BillingContent = () => (
  <Section title="Plan & payment">
    <Row label="Current plan"    sub="Startup Accelerator"              right={<Btn variant="primary">Upgrade</Btn>} />
    <Row label="Payment method"  sub="Not configured"                   right={<Btn variant="ghost">Update</Btn>} />
    <Row label="Billing history" sub="View past invoices and payments"  right={<Btn variant="ghost">View</Btn>} last />
  </Section>
)

const DataExportContent = () => (
  <Section title="Export your data">
    <Row label="Interview data"       sub="All recordings and feedback"           right={<Btn variant="ghost">Export</Btn>} />
    <Row label="Candidate data"       sub="Profiles and assessments"              right={<Btn variant="ghost">Export</Btn>} />
    <Row label="Reports & analytics"  sub="Hiring reports and metrics"            right={<Btn variant="ghost">Export</Btn>} last />
  </Section>
)

const DeleteAccountContent = () => (
  <div>
    <Alert type="warn">
      This is permanent. All interview recordings, candidate profiles, team settings, and billing data will be deleted and cannot be recovered.
    </Alert>
    <Section title="What gets deleted" style={{ marginTop: 28 }}>
      {[
        "All interview recordings and transcripts",
        "Candidate profiles and assessment data",
        "Team settings and member permissions",
        "Billing information and subscription history",
      ].map((item, i, arr) => (
        <Row key={i} label={item} last={i === arr.length - 1} />
      ))}
    </Section>
    <Btn variant="danger"><Trash2 size={12} /> Delete my account</Btn>
  </div>
)

// ── Nav config ────────────────────────────────────────────────────────────────

const NAV = [
  { group: "Workspace", items: [
    { id: "Integrations",    label: "Integrations"    },
    { id: "Email Templates", label: "Email templates" },
  ]},
  { group: "Account", items: [
    { id: "Notifications",   label: "Notifications"   },
    { id: "Security",        label: "Security"        },
  ]},
  { group: "Plan", items: [
    { id: "Billing",         label: "Billing"         },
    { id: "Data Export",     label: "Data export"     },
  ]},
  { group: null, items: [
    { id: "Delete Account",  label: "Delete account", danger: true },
  ]},
]

const ALL_ITEMS = NAV.flatMap(g => g.items)

// ── Main ──────────────────────────────────────────────────────────────────────

const SettingsTab = ({ user }) => {
  const [active, setActive] = useState("Integrations")
  const { organizationId }  = useUser()
  const [notifications, setNotifications] = useState({
    communicationEmails: true,
    announcements:       true,
    interviewReminders:  true,
    candidateUpdates:    true,
  })

  const renderContent = () => {
    switch (active) {
      case "Integrations":    return <IntegrationsContent />
      case "Email Templates": return <EmailTemplatesContent userId={user?.uid} />
      case "Security":        return <SecurityContent />
      case "Notifications":   return <NotificationsContent notifications={notifications} onChange={(k, v) => setNotifications(p => ({ ...p, [k]: v }))} />
      case "Billing":         return <BillingContent />
      case "Data Export":     return <DataExportContent />
      case "Delete Account":  return <DeleteAccountContent />
      default:                return null
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
      `}</style>

      {/* App sidebar */}
      <div style={{
        position: "fixed", top: 0, left: 0,
        width: "15rem", height: "100vh",
        borderRight: T.border, background: "#f3f3f3",
        overflowY: "auto", zIndex: 10,
      }}>
        <ProfileSidebar activeTab="settings" setActiveTab={() => {}} showMobileHeader={false} />
      </div>

      <div style={{ marginLeft: "15rem", display: "flex", minHeight: "100vh" }}>

        {/* Settings sub-nav */}
        <div style={{ width: 196, borderRight: T.border, padding: "32px 0", flexShrink: 0 }}>
          <p style={{ margin: "0 0 20px", padding: "0 20px", fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FF }}>
            Settings
          </p>

          {NAV.map((group, gi) => (
            <div key={gi} style={{ marginBottom: group.group ? 20 : 4 }}>
              {group.group && (
                <p style={{ margin: "0 0 2px", padding: "0 20px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.09em", color: T.textSub, fontFamily: FF }}>
                  {group.group}
                </p>
              )}
              {group.items.map(item => {
                const isActive = active === item.id
                return (
                  <button
                    key={item.id}
                    onClick={() => setActive(item.id)}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "5px 20px",
                      background: "none", border: "none",
                      borderLeft: isActive ? "2px solid #111827" : "2px solid transparent",
                      color: item.danger ? "#dc2626" : isActive ? T.text : "#6b7280",
                      fontSize: 13, fontWeight: isActive ? 600 : 400, fontFamily: FF,
                      cursor: "pointer", outline: "none",
                      transition: "color 0.1s, border-color 0.1s",
                    }}
                    onMouseEnter={(e) => { if (!isActive && !item.danger) e.currentTarget.style.color = T.text }}
                    onMouseLeave={(e) => { if (!isActive && !item.danger) e.currentTarget.style.color = "#6b7280" }}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: "32px 52px", overflowY: "auto" }}>
          <div style={{ maxWidth: 600 }}>
            <h2 style={{ margin: "0 0 28px", fontSize: 22, fontWeight: 700, color: T.text, fontFamily: FF, letterSpacing: "-0.02em" }}>
              {ALL_ITEMS.find(i => i.id === active)?.label}
            </h2>
            {renderContent()}
          </div>
        </div>

      </div>
    </div>
  )
}

export default SettingsTab
